/* eslint-disable no-unknown/no-unknown */
// @services/clients/ListingInferenceReviewPortalApi.ts
import * as E from "fp-ts/Either";
import * as t from "io-ts";
import { PathReporter } from "io-ts/PathReporter";

import type { OptionTs } from "@ps-bridge/interop";
import type {
    ListingInferenceAgentRequestId,
    ListingInferenceAgentTraceId,
    ListingInferenceClaimId,
    ListingInferenceClaimKind,
    ListingInferenceDecisionReason,
    ListingInferenceDisposition,
    ListingInferenceListingId,
    ListingInferenceReviewer,
    ListingInferenceSourceRef,
} from "@ps-bridge/models/brands/listingInferenceReviewPortal";
import {
    listingInferenceAgentRequestIdCodec,
    listingInferenceAgentTraceIdCodec,
    listingInferenceClaimIdCodec,
    listingInferenceClaimKindCodec,
    listingInferenceDecisionReasonCodec,
    listingInferenceDispositionCodec,
    listingInferenceListingIdCodec,
    listingInferenceReviewerCodec,
    listingInferenceSourceRefCodec,
} from "@utils/listingInferenceReviewPortalBrands";
import * as Option from "@utils/Option";

export const ListingInferenceReviewPortalContract = {
    contractId: "listing.inference.reviewPortal",
    reviewWorkspacePath: "/v1/listings/{listingId}/inference/review-workspace",
    evidencePath: "/v1/listings/{listingId}/inference/evidence",
    reviewDecisionsPath: "/v1/listings/{listingId}/inference/review-decisions",
} as const;

export type ListingInferenceApiDecodeResult<T> =
    | {
          readonly type: "ok";
          readonly value: T;
      }
    | {
          readonly type: "error";
          readonly message: string;
      };

export type ListingInferenceJsonPayload =
    | string
    | number
    | boolean
    | object
    | readonly ListingInferenceJsonPayload[];

export type ListingInferenceNonFactStatus =
    | "evidenceOnly"
    | "containsUnresolvedGaps"
    | "containsRejectedOrConflictedClaims"
    | "refusedUnsafeFactPromotion";

export type ListingInferenceReviewErrorCode =
    | "invalidListingId"
    | "workspaceNotFound"
    | "readDenied"
    | "writeDenied"
    | "invalidDecision"
    | "unsafeFactPromotionRefused"
    | "serviceUnavailable"
    | "networkError"
    | "responseDecodeError";

export type ListingInferenceRequestTrace = {
    readonly traceId: ListingInferenceAgentTraceId;
    readonly requestId: ListingInferenceAgentRequestId;
};

export type ListingInferenceSpanAnchor = {
    readonly startMs: number;
    readonly endMs: number;
};

export type ListingInferenceProvenance = {
    readonly sourceRef: ListingInferenceSourceRef;
    readonly span: ListingInferenceSpanAnchor;
    readonly speaker: string;
    readonly observedAt: string;
};

export type ListingInferenceProvisionalClaim = {
    readonly id: ListingInferenceClaimId;
    readonly listingId: ListingInferenceListingId;
    readonly kind: ListingInferenceClaimKind;
    readonly value: string;
    readonly disposition: ListingInferenceDisposition;
    readonly provenance: ListingInferenceProvenance;
};

export type ListingInferenceGap = {
    readonly question: string;
    readonly listingId: ListingInferenceListingId;
    readonly evidenceSource: ListingInferenceSourceRef;
    readonly provenance: ListingInferenceProvenance;
};

export type ListingInferenceReview = {
    readonly item: ListingInferenceGap;
    readonly owner: string;
    readonly reason: string;
    readonly dueAt: string;
};

export type ListingInferenceReviewDecision = {
    readonly listingId: ListingInferenceListingId;
    readonly claimId: ListingInferenceClaimId;
    readonly reviewer: ListingInferenceReviewer;
    readonly targetDisposition: ListingInferenceDisposition;
    readonly reason: ListingInferenceDecisionReason;
    readonly decidedAt: string;
};

export type ListingInferenceClaimDispositionSummary = {
    readonly proposed: number;
    readonly confirmed: number;
    readonly rejected: number;
    readonly conflicted: number;
};

export type ListingInferenceReviewWorkspace = {
    readonly listingId: ListingInferenceListingId;
    readonly claims: readonly ListingInferenceProvisionalClaim[];
    readonly outstandingReviews: readonly ListingInferenceReview[];
    readonly gaps: readonly ListingInferenceGap[];
    readonly manualDecisions: readonly ListingInferenceReviewDecision[];
    readonly effectiveDispositionSummary: ListingInferenceClaimDispositionSummary;
};

export type ListingInferenceWorkspaceReadResult = {
    readonly listingId: ListingInferenceListingId;
    readonly workspace: ListingInferenceReviewWorkspace;
    readonly claims: readonly ListingInferenceProvisionalClaim[];
    readonly outstandingReviews: readonly ListingInferenceReview[];
    readonly gaps: readonly ListingInferenceGap[];
    readonly manualDecisions: readonly ListingInferenceReviewDecision[];
    readonly effectiveDispositionSummary: ListingInferenceClaimDispositionSummary;
    readonly nonFactStatus: ListingInferenceNonFactStatus;
    readonly trace: ListingInferenceRequestTrace;
};

export type ListingInferenceFiltersApplied = {
    readonly claimKinds: readonly ListingInferenceClaimKind[];
    readonly dispositions: readonly ListingInferenceDisposition[];
    readonly sourceRefs: readonly ListingInferenceSourceRef[];
    readonly unresolvedOnly: boolean;
    readonly includeGaps: boolean;
};

export type ListingInferenceEvidenceRow = {
    readonly claimId: ListingInferenceClaimId;
    readonly listingId: ListingInferenceListingId;
    readonly kind: ListingInferenceClaimKind;
    readonly value: string;
    readonly disposition: ListingInferenceDisposition;
    readonly provenance: ListingInferenceProvenance;
    readonly manualDecision: OptionTs<ListingInferenceReviewDecision>;
    readonly nonFactStatus: ListingInferenceNonFactStatus;
};

export type ListingInferenceGapRow = {
    readonly gap: ListingInferenceGap;
    readonly relatedClaimId: OptionTs<ListingInferenceClaimId>;
    readonly unresolved: boolean;
    readonly rejectedOrConflicted: boolean;
    readonly provenance: OptionTs<ListingInferenceProvenance>;
    readonly nonFactStatus: ListingInferenceNonFactStatus;
};

export type ListingInferenceEvidenceQuery = {
    readonly listingId: ListingInferenceListingId;
    readonly claimKinds: readonly ListingInferenceClaimKind[];
    readonly dispositions: readonly ListingInferenceDisposition[];
    readonly sourceRefs: readonly ListingInferenceSourceRef[];
    readonly unresolvedOnly: boolean;
    readonly includeGaps: boolean;
    readonly unsafeFactPromotion: boolean;
};

export type ListingInferenceEvidenceQueryResult = {
    readonly listingId: ListingInferenceListingId;
    readonly evidenceRows: readonly ListingInferenceEvidenceRow[];
    readonly gapRows: readonly ListingInferenceGapRow[];
    readonly filtersApplied: ListingInferenceFiltersApplied;
    readonly nonFactStatus: ListingInferenceNonFactStatus;
    readonly trace: ListingInferenceRequestTrace;
};

export type ListingInferenceManualDecisionRequest = {
    readonly listingId: ListingInferenceListingId;
    readonly claimId: ListingInferenceClaimId;
    readonly targetDisposition: ListingInferenceDisposition;
    readonly reviewer: ListingInferenceReviewer;
    readonly reason: ListingInferenceDecisionReason;
    readonly unsafeFactPromotion: boolean;
};

export type ListingInferenceAcceptedDecision = {
    readonly listingId: ListingInferenceListingId;
    readonly claimId: ListingInferenceClaimId;
    readonly reviewer: ListingInferenceReviewer;
    readonly targetDisposition: ListingInferenceDisposition;
    readonly reason: ListingInferenceDecisionReason;
    readonly decidedAt: string;
};

export type ListingInferenceWorkspaceRefresh =
    | {
          readonly kind: "included";
          readonly workspace: ListingInferenceWorkspaceReadResult;
      }
    | {
          readonly kind: "required";
          readonly listingId: ListingInferenceListingId;
          readonly trace: ListingInferenceRequestTrace;
      };

export type ListingInferenceManualDecisionResult = {
    readonly acceptedDecision: ListingInferenceAcceptedDecision;
    readonly effectiveDispositionSummary: ListingInferenceClaimDispositionSummary;
    readonly refresh: ListingInferenceWorkspaceRefresh;
};

export type ListingInferenceRefusal = {
    readonly error: Exclude<ListingInferenceReviewErrorCode, "networkError" | "responseDecodeError">;
    readonly message: string;
    readonly trace: ListingInferenceRequestTrace;
};

export type ListingInferenceReviewError =
    | ListingInferenceRefusal
    | {
          readonly error: "networkError" | "responseDecodeError";
          readonly message: string;
          readonly trace: OptionTs<ListingInferenceRequestTrace>;
      };

const jsonNull = (): ListingInferenceJsonPayload => JSON.parse("null");

const validationSucceeded = <A>(validation: t.Validation<A>): boolean => validation._tag === "Right";

const fromStringInput = <A>(codec: t.Type<A, string, string>): t.Type<A, string, unknown> =>
    new t.Type<A, string, unknown>(
        codec.name,
        codec.is,
        (input, context) => {
            if (typeof input !== "string") {
                return t.failure(input, context, `${codec.name} must be a string`);
            }
            const validated = codec.validate(input, context);
            return validationSucceeded(validated)
                ? t.success(input.trim() as A)
                : validated;
        },
        codec.encode,
    );

const listingIdCodec = fromStringInput(listingInferenceListingIdCodec);
const claimIdCodec = fromStringInput(listingInferenceClaimIdCodec);
const claimKindCodec = fromStringInput(listingInferenceClaimKindCodec);
const dispositionCodec = fromStringInput(listingInferenceDispositionCodec);
const reviewerCodec = fromStringInput(listingInferenceReviewerCodec);
const decisionReasonCodec = fromStringInput(listingInferenceDecisionReasonCodec);
const sourceRefCodec = fromStringInput(listingInferenceSourceRefCodec);
const agentRequestIdCodec = fromStringInput(listingInferenceAgentRequestIdCodec);
const agentTraceIdCodec = fromStringInput(listingInferenceAgentTraceIdCodec);

const nonFactStatusCodec = t.keyof({
    evidenceOnly: 0,
    containsUnresolvedGaps: 0,
    containsRejectedOrConflictedClaims: 0,
    refusedUnsafeFactPromotion: 0,
});

const reviewErrorCodeCodec = t.keyof({
    invalidListingId: 0,
    workspaceNotFound: 0,
    readDenied: 0,
    writeDenied: 0,
    invalidDecision: 0,
    unsafeFactPromotionRefused: 0,
    serviceUnavailable: 0,
});

const optionCodec = <A>(codec: t.Decoder<unknown, A>): t.Type<OptionTs<A>, unknown, unknown> =>
    new t.Type<OptionTs<A>, unknown, unknown>(
        "Option",
        (value): value is OptionTs<A> => typeof value === "object",
        (input, context) => {
            if (input === jsonNull()) {
                return t.success(Option.none<A>());
            }
            const decoded = codec.decode(input);
            if (E.isRight(decoded)) {
                return t.success(Option.some(decoded.right));
            }
            return t.failure(input, context, PathReporter.report(decoded).join("; "));
        },
        (value) => Option.fold(value, jsonNull, (inner) => inner as ListingInferenceJsonPayload),
    );

export const listingInferenceRequestTraceCodec = t.type({
    traceId: agentTraceIdCodec,
    requestId: agentRequestIdCodec,
});

export const listingInferenceSpanAnchorCodec = t.type({
    startMs: t.number,
    endMs: t.number,
});

export const listingInferenceProvenanceCodec = t.type({
    sourceRef: sourceRefCodec,
    span: listingInferenceSpanAnchorCodec,
    speaker: t.string,
    observedAt: t.string,
});

export const listingInferenceProvisionalClaimCodec = t.type({
    id: claimIdCodec,
    listingId: listingIdCodec,
    kind: claimKindCodec,
    value: t.string,
    disposition: dispositionCodec,
    provenance: listingInferenceProvenanceCodec,
});

export const listingInferenceGapCodec = t.type({
    question: t.string,
    listingId: listingIdCodec,
    evidenceSource: sourceRefCodec,
    provenance: listingInferenceProvenanceCodec,
});

export const listingInferenceReviewCodec = t.type({
    item: listingInferenceGapCodec,
    owner: t.string,
    reason: t.string,
    dueAt: t.string,
});

export const listingInferenceReviewDecisionCodec = t.type({
    listingId: listingIdCodec,
    claimId: claimIdCodec,
    reviewer: reviewerCodec,
    targetDisposition: dispositionCodec,
    reason: decisionReasonCodec,
    decidedAt: t.string,
});

export const listingInferenceClaimDispositionSummaryCodec = t.type({
    proposed: t.number,
    confirmed: t.number,
    rejected: t.number,
    conflicted: t.number,
});

export const listingInferenceReviewWorkspaceCodec = t.type({
    listingId: listingIdCodec,
    claims: t.array(listingInferenceProvisionalClaimCodec),
    outstandingReviews: t.array(listingInferenceReviewCodec),
    gaps: t.array(listingInferenceGapCodec),
    manualDecisions: t.array(listingInferenceReviewDecisionCodec),
    effectiveDispositionSummary: listingInferenceClaimDispositionSummaryCodec,
});

export const listingInferenceWorkspaceReadResultCodec = t.type({
    listingId: listingIdCodec,
    workspace: listingInferenceReviewWorkspaceCodec,
    claims: t.array(listingInferenceProvisionalClaimCodec),
    outstandingReviews: t.array(listingInferenceReviewCodec),
    gaps: t.array(listingInferenceGapCodec),
    manualDecisions: t.array(listingInferenceReviewDecisionCodec),
    effectiveDispositionSummary: listingInferenceClaimDispositionSummaryCodec,
    nonFactStatus: nonFactStatusCodec,
    trace: listingInferenceRequestTraceCodec,
});

export const listingInferenceFiltersAppliedCodec = t.type({
    claimKinds: t.array(claimKindCodec),
    dispositions: t.array(dispositionCodec),
    sourceRefs: t.array(sourceRefCodec),
    unresolvedOnly: t.boolean,
    includeGaps: t.boolean,
});

export const listingInferenceEvidenceRowCodec = t.type({
    claimId: claimIdCodec,
    listingId: listingIdCodec,
    kind: claimKindCodec,
    value: t.string,
    disposition: dispositionCodec,
    provenance: listingInferenceProvenanceCodec,
    manualDecision: optionCodec(listingInferenceReviewDecisionCodec),
    nonFactStatus: nonFactStatusCodec,
});

export const listingInferenceGapRowCodec = t.type({
    gap: listingInferenceGapCodec,
    relatedClaimId: optionCodec(claimIdCodec),
    unresolved: t.boolean,
    rejectedOrConflicted: t.boolean,
    provenance: optionCodec(listingInferenceProvenanceCodec),
    nonFactStatus: nonFactStatusCodec,
});

export const listingInferenceEvidenceQueryResultCodec = t.type({
    listingId: listingIdCodec,
    evidenceRows: t.array(listingInferenceEvidenceRowCodec),
    gapRows: t.array(listingInferenceGapRowCodec),
    filtersApplied: listingInferenceFiltersAppliedCodec,
    nonFactStatus: nonFactStatusCodec,
    trace: listingInferenceRequestTraceCodec,
});

export const listingInferenceAcceptedDecisionCodec = t.type({
    listingId: listingIdCodec,
    claimId: claimIdCodec,
    reviewer: reviewerCodec,
    targetDisposition: dispositionCodec,
    reason: decisionReasonCodec,
    decidedAt: t.string,
});

const listingInferenceWorkspaceRefreshIncludedCodec = t.type({
    kind: t.literal("included"),
    workspace: listingInferenceWorkspaceReadResultCodec,
});

const listingInferenceWorkspaceRefreshRequiredCodec = t.type({
    kind: t.literal("required"),
    listingId: listingIdCodec,
    trace: listingInferenceRequestTraceCodec,
});

export const listingInferenceWorkspaceRefreshCodec = t.union([
    listingInferenceWorkspaceRefreshIncludedCodec,
    listingInferenceWorkspaceRefreshRequiredCodec,
]);

export const listingInferenceManualDecisionResultCodec = t.type({
    acceptedDecision: listingInferenceAcceptedDecisionCodec,
    effectiveDispositionSummary: listingInferenceClaimDispositionSummaryCodec,
    refresh: listingInferenceWorkspaceRefreshCodec,
});

export const listingInferenceRefusalCodec = t.type({
    error: reviewErrorCodeCodec,
    message: t.string,
    trace: listingInferenceRequestTraceCodec,
});

const decodeWith = <T>(codec: t.Decoder<unknown, T>, payload: unknown, label: string): ListingInferenceApiDecodeResult<T> => {
    const decoded = codec.decode(payload);
    if (E.isRight(decoded)) {
        return {
            type: "ok",
            value: decoded.right,
        };
    }

    return {
        type: "error",
        message: `${label} decode failed: ${PathReporter.report(decoded).join("; ")}`,
    };
};

export const decodeListingInferenceWorkspaceReadResult = (
    payload: unknown,
): ListingInferenceApiDecodeResult<ListingInferenceWorkspaceReadResult> =>
    decodeWith(listingInferenceWorkspaceReadResultCodec, payload, "listing inference workspace read result");

export const decodeListingInferenceEvidenceQueryResult = (
    payload: unknown,
): ListingInferenceApiDecodeResult<ListingInferenceEvidenceQueryResult> =>
    decodeWith(listingInferenceEvidenceQueryResultCodec, payload, "listing inference evidence query result");

export const decodeListingInferenceManualDecisionResult = (
    payload: unknown,
): ListingInferenceApiDecodeResult<ListingInferenceManualDecisionResult> =>
    decodeWith(listingInferenceManualDecisionResultCodec, payload, "listing inference manual decision result");

export const decodeListingInferenceRefusal = (
    payload: unknown,
): ListingInferenceApiDecodeResult<ListingInferenceRefusal> =>
    decodeWith(listingInferenceRefusalCodec, payload, "listing inference refusal");

export const encodeListingInferenceManualDecisionRequest = (
    request: ListingInferenceManualDecisionRequest,
): Record<string, string | boolean> => ({
    listingId: request.listingId,
    claimId: request.claimId,
    targetDisposition: request.targetDisposition,
    reviewer: request.reviewer,
    reason: request.reason,
    unsafeFactPromotion: request.unsafeFactPromotion,
});

export const encodeListingInferenceEvidenceQueryParams = (
    query: ListingInferenceEvidenceQuery,
): URLSearchParams => {
    const params = new URLSearchParams();
    if (query.claimKinds.length > 0) {
        params.set("claimKind", query.claimKinds.join(","));
    }
    if (query.dispositions.length > 0) {
        params.set("disposition", query.dispositions.join(","));
    }
    if (query.sourceRefs.length > 0) {
        params.set("sourceRef", query.sourceRefs.join(","));
    }
    params.set("unresolvedOnly", String(query.unresolvedOnly));
    params.set("includeGaps", String(query.includeGaps));
    params.set("unsafeFactPromotion", String(query.unsafeFactPromotion));
    return params;
};
